import { left, right } from 'fp-ts/Either';

import { httpClientFunction } from 'domain/types';
import Layout, { LayoutType, LayoutElement, LayoutElementType } from 'domain/entity/layout';
import ReadLayoutsClient from 'use-case/layout/read/readLayoutsClient';
import ReadLayoutsCriteria from 'use-case/layout/read/readLayoutsCriteria';

const readLayoutsApiClient = (httpClient: httpClientFunction, baseUrl: string): ReadLayoutsClient => (criteria: ReadLayoutsCriteria) =>
    httpClient(`${baseUrl}/dashboards/dashboards/${criteria.dashboardId}/layouts`, {})
        .then((response) =>
            response.ok
                ? response
                      .json()
                      .then(asLayouts)
                      .then(right)
                      .catch(() => left({ error: new Error(), status: 500 }))
                : left({ error: new Error(), status: response.status })
        )
        .catch(() => left({ error: new Error(), status: 503 }));

const asLayouts = (layoutsJson: LayoutJson[]) =>
    layoutsJson.map((layoutItem: LayoutJson) => new Layout(layoutItem.type as LayoutType, asLayoutElements(layoutItem.widgets)));

const asLayoutElements = (layoutWidgetElementsJson: LayoutElementJson[]): LayoutElement[] =>
    layoutWidgetElementsJson.map((element) => ({
        id: element.id,
        position: element.position,
        size: element.size,
        type: element.type as LayoutElementType,
        constraints: {
            minWidth: element.size_constraints.min_width,
            maxWidth: element.size_constraints.max_width,
            minHeight: element.size_constraints.min_height,
            maxHeight: element.size_constraints.max_height,
        },
    }));

export type LayoutJson = {
    type: string;
    widgets: LayoutElementJson[];
};

export type LayoutElementJson = {
    id: string;
    position: { x: number; y: number };
    type: string;
    size: { width: number; height: number };
    ['size_constraints']: {
        ['min_width']: number;
        ['min_height']: number;
        ['max_width']: number;
        ['max_height']: number;
    };
};

export default readLayoutsApiClient;
